import { useState } from "react";
import { createProject } from "@/lib/api";
import type { Project } from "@/lib/types";
import { Banner, Button, Spinner } from "./ui";
import { useLanguage } from "@/i18n";

export default function NewProjectForm({ onCreated }: { onCreated: (p: Project) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const project = await createProject({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      onCreated(project);
      setName("");
      setDescription("");
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.newProjectForm.couldntCreateProject);
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <Button variant="primary" onClick={() => setOpen(true)}>
        {t.newProjectForm.newProject}
      </Button>
    );
  }

  return (
    // The open form floats over the page instead of taking part in the header's
    // layout. Inline, it was 32px taller than the button it replaced, so every
    // create sequence visibly shoved the summary tiles and the whole project
    // table down 32px on open and snapped them back up on submit -- read as the
    // page "flickering" while creating a project. The invisible button below
    // reserves exactly the closed state's footprint, so the header's height is
    // now identical open or closed and nothing moves.
    <div className="relative">
      <Button variant="primary" className="invisible" aria-hidden="true" tabIndex={-1}>
        {t.newProjectForm.newProject}
      </Button>
      <form
        onSubmit={submit}
        className="absolute right-0 top-0 z-30 w-[min(36rem,88vw)] rounded-md bg-surface p-3 shadow-ring"
      >
        <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-[12rem] flex-1">
          <span className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-ink-muted">
            {t.newProjectForm.projectNameLabel}
          </span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.newProjectForm.projectNamePlaceholder}
            className="h-9 w-full rounded border-0 bg-surface-sunken px-2.5 text-sm text-ink shadow-ring placeholder:text-ink-muted focus:bg-surface-raised"
          />
        </label>
        <label className="min-w-[12rem] flex-1">
          <span className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-ink-muted">
            {t.newProjectForm.descriptionLabel}{" "}
            <span className="font-normal normal-case">{t.newProjectForm.optional}</span>
          </span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t.newProjectForm.descriptionPlaceholder}
            className="h-9 w-full rounded border-0 bg-surface-sunken px-2.5 text-sm text-ink shadow-ring placeholder:text-ink-muted focus:bg-surface-raised"
          />
        </label>
        <div className="flex gap-1.5">
          <Button type="submit" variant="primary" disabled={saving || !name.trim()}>
            {saving ? <Spinner className="h-3.5 w-3.5" /> : null}
            {saving ? t.newProjectForm.creating : t.newProjectForm.create}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setOpen(false);
              setError(null);
            }}
          >
            {t.newProjectForm.cancel}
          </Button>
        </div>
      </div>
        {error && (
          <div className="mt-2">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}
      </form>
    </div>
  );
}
